import { TagState } from '@custom-components';

/**
 * Centralized value → TagState mappings for `xemex-tag` table columns.
 *
 * Each mapper turns a raw domain value into the tag's visual state. Call these
 * at the row-mapping site (e.g. inside the table's `tableData` computed) and
 * store the result in the row field referenced by the column's `stateField`.
 * Unknown values fall back to `'neutral'`.
 */
export const TagStateOf = {
  activityStatus: (status: string): TagState =>
    ({ Success: 'success', Failed: 'error', 'In Progress': 'warning', Pending: 'neutral' } as Record<string, TagState>)[
      status
    ] ?? 'neutral',

  alarmPriority: (priority: string): TagState =>
    ({ High: 'error', Medium: 'warning', Low: 'info' } as Record<string, TagState>)[priority] ?? 'neutral',

  firmwareStatus: (status: string): TagState =>
    ({
      'Up to date': 'success',
      'Update available': 'warning',
      'Unknown version': 'info',
      Unknown: 'neutral',
    } as Record<string, TagState>)[status] ?? 'neutral',

  firmwareActive: (status: string): TagState => (status === 'Active' ? 'success' : 'neutral'),

  shipmentStatus: (status: string): TagState =>
    ({ Complete: 'success', Processing: 'warning', Error: 'error' } as Record<string, TagState>)[status] ?? 'neutral',
} as const;

/** Same High/Medium/Low → semantic mapping as `TagStateOf.alarmPriority`, as a raw hex for
 * contexts needing a solid color (e.g. chart/dot accents, a table's read-only `accentColorMap`)
 * rather than an `xemex-tag`'s `TagState`. High reuses `$token-status-error-text` (see
 * `_tokens.generated.scss`); Medium uses a dedicated, more vivid accent (#E6A333) rather than the
 * muted `$token-status-warning-text`; Low uses the distinct cyan solid (`core.color.cyan.60`,
 * #06B6D4) rather than `$token-status-info-text` (#0E7490), per design. */
export const ALARM_PRIORITY_ACCENT_COLOR: Record<string, string> = {
  High: '#BA1A1A',
  Medium: '#E6A333',
  Low: '#06B6D4',
};
